/**
 * Who gets yesterday's bread.
 *
 * The shelf is finite. A few loaves survive the previous open day and the rest
 * of the morning's orders are baked fresh, so somebody has to decide which
 * customer is handed the older one — and whoever is packing needs to know
 * before the customer does.
 *
 * Left alone, the sheet hands them to the orders nearest the top and lets them
 * run out partway down. That is stable and visible, which is most of what the
 * packing bench needs, but it is still only an accident of sort order. The
 * shop knows that the seven o'clock collection should take the day-old loaf
 * and the four o'clock one should not, so naming an order moves it to the
 * front of the queue.
 *
 * Allocation is per unit, not per order: one loaf left against an order for
 * two means one off the shelf and one out of the oven, and the sheet says so
 * rather than rounding the decision either way.
 */
/**
 * How many units each order takes off the shelf.
 *
 * Orders that take none are left out, so the result reads as "these are the
 * ones holding older bread" rather than a row per order.
 */
export function allocateShelf(input) {
    const taken = new Map();
    let remaining = Math.max(0, Math.floor(input.carriedIn));
    if (remaining === 0)
        return taken;
    const wanted = new Map();
    for (const line of input.orders) {
        if (line.qty > 0)
            wanted.set(line.orderId, (wanted.get(line.orderId) ?? 0) + line.qty);
    }
    /*
     * Named orders first, in the order they were named, then everyone else in
     * the order the sheet shows. A claimed order that wants none of this product
     * — or that has since been cancelled off the day — simply contributes
     * nothing and does not hold up the queue behind it.
     */
    const queue = [
        ...input.claim.filter((orderId) => wanted.has(orderId)),
        ...input.orders.map((line) => line.orderId).filter((orderId) => !input.claim.includes(orderId)),
    ];
    const served = new Set();
    for (const orderId of queue) {
        if (remaining === 0)
            break;
        if (served.has(orderId))
            continue;
        served.add(orderId);
        const want = wanted.get(orderId) ?? 0;
        if (want === 0)
            continue;
        const give = Math.min(want, remaining);
        taken.set(orderId, give);
        remaining -= give;
    }
    return taken;
}
//# sourceMappingURL=shelf.js.map